import React, { useEffect, useRef, useState } from "react";
import { api, API_BASE } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { pcbGet, pcbRemove, pcbClear, pcbSubscribe, canBuildCatalog } from "../../lib/pcb";
import { FileText, X, Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * CatalogBuilderBar — floating bar visible only to logged-in staff
 * (salesperson / admin / major admin / super admin) while they browse the
 * public storefront. Shows the pieces added to the Personal Catalog Builder
 * and generates the branded PDF (cover image, product ID, salesperson ID,
 * today's rate for the selected purities, timestamp + disclaimer note).
 */
export default function CatalogBuilderBar() {
  const { user } = useAuth();
  const [items, setItems] = useState(pcbGet());
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [cust, setCust] = useState({ name: "", phone: "" });
  const [footerVisible, setFooterVisible] = useState(false); // hide the pill while the footer is on screen
  const [dismissed, setDismissed] = useState(false);         // cancel (X) hides the pill until a new item is added
  const prevLen = useRef(items.length);

  useEffect(() => pcbSubscribe(() => setItems(pcbGet())), []);

  // When a new item is added, bring the pill back even if it was cancelled.
  useEffect(() => {
    if (items.length > prevLen.current) setDismissed(false);
    prevLen.current = items.length;
  }, [items.length]);

  // Watch the storefront footer — when it scrolls into view, hide the floating
  // pill so it never overlaps the footer (fixes the mobile footer overlap).
  useEffect(() => {
    const footer = document.querySelector('footer[data-testid="storefront-footer"]');
    if (!footer) return;
    const io = new IntersectionObserver(
      ([entry]) => setFooterVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    io.observe(footer);
    return () => io.disconnect();
  }, [items.length]);

  if (!user || !canBuildCatalog(user.role) || items.length === 0) return null;

  const showPill = !open && !footerVisible && !dismissed;

  const generatePdf = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/api/crm/salesperson/catalog", {
        customer_name: cust.name || "Walk-in Guest",
        customer_phone: cust.phone || "",
        product_ids: items.map((p) => p.id),
        notes: "",
      });
      // open the generated PDF (auth token via api base — use fetch to blob for auth header)
      const resp = await api.get(`/api/crm/salesperson/catalog/${data.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `SG-Catalog-${data.id.slice(0, 8)}.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success("Personal catalog PDF generated");
      pcbClear();
      setOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to generate PDF");
    } finally { setBusy(false); }
  };

  return (
    <>
      {/* Floating pill — compact, with its own cancel (X). Auto-hides while the
          footer is on screen so it never covers footer links on mobile. */}
      {showPill && (
        <div
          className="fixed bottom-6 left-6 z-[65] flex items-stretch shadow-xl transition-opacity duration-200"
          data-testid="pcb-bar-wrap"
        >
          <button
            onClick={() => setOpen(true)}
            className="bg-maroon text-pearl pl-3 pr-2.5 py-2.5 flex items-center gap-2 text-sm"
            aria-label={`Open personal catalog (${items.length} items)`}
            data-testid="pcb-bar"
          >
            <FileText size={16} />
            <span className="hidden sm:inline">Personal Catalog</span>
            <span className="bg-gold text-ink px-1.5 text-xs font-semibold">{items.length}</span>
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); setDismissed(true); }}
            className="bg-maroon-dark text-pearl/80 hover:text-pearl px-2 border-l border-pearl/20"
            aria-label="Hide personal catalog button"
            data-testid="pcb-bar-cancel"
          >
            <X size={14} />
          </button>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/40 z-[70] flex items-end lg:items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="bg-white border border-line w-full max-w-lg max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="p-4 border-b border-line flex items-center justify-between">
              <div className="font-display text-2xl text-maroon">Personal Catalog Builder</div>
              <button onClick={() => setOpen(false)} data-testid="pcb-close"><X size={18}/></button>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {items.map((p) => (
                <div key={p.id} className="flex items-center gap-3 border border-line p-2" data-testid={`pcb-item-${p.id}`}>
                  {p.image ? <img src={p.image} alt="" className="w-12 h-12 object-cover bg-pearl"/> : <div className="w-12 h-12 bg-pearl"/>}
                  <div className="flex-1">
                    <div className="text-sm font-medium">{p.name}</div>
                    <div className="text-[10px] text-ash">{p.metal} {p.purity} · {p.weight_grams}g · ID {p.id.slice(0, 8).toUpperCase()}</div>
                  </div>
                  <button onClick={() => pcbRemove(p.id)} className="text-ash hover:text-red-600" data-testid={`pcb-remove-${p.id}`}><Trash2 size={14}/></button>
                </div>
              ))}
            </div>
            <div className="p-4 border-t border-line space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input placeholder="Customer name (optional)" className="border border-line px-3 py-2 bg-pearl text-sm" value={cust.name} onChange={(e) => setCust({...cust, name: e.target.value})} data-testid="pcb-cust-name"/>
                <input placeholder="Customer phone (optional)" className="border border-line px-3 py-2 bg-pearl text-sm" value={cust.phone} onChange={(e) => setCust({...cust, phone: e.target.value.replace(/\D/g, "").slice(0, 10)})} data-testid="pcb-cust-phone"/>
              </div>
              <div className="flex gap-2">
                <button onClick={() => { pcbClear(); setOpen(false); }} className="btn-outline text-xs flex-1 justify-center" data-testid="pcb-clear">Clear All</button>
                <button onClick={generatePdf} disabled={busy} className="btn-maroon text-xs flex-[2] justify-center" data-testid="pcb-generate">
                  {busy ? <Loader2 className="animate-spin" size={14}/> : <FileText size={14}/>} Generate PDF
                </button>
              </div>
              <p className="text-[10px] text-ash">The PDF includes cover images, product IDs, your salesperson ID, today's rate for the selected purities, and the date/time stamp.</p>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
